// src/components/Navbar.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import logoImage from '../assets/logo.png';

/* ===============================
   NAVBAR STYLES
================================*/

const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 2000;

  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: ${props => (props.scrolled ? "0.6rem 10%" : "1rem 10%")};
  transition: all 0.25s ease;

  background: rgba(15, 17, 35, 0.6);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);

  border-bottom: 1px solid rgba(255,255,255,0.08);
  box-shadow: ${props =>
    props.scrolled 
      ? "0 8px 25px rgba(0,0,0,0.35)"
      : "0 0 0 transparent"};

  @media (max-width: 768px) {
    padding: 0.8rem 7%;
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  text-decoration: none;
`;

const LogoImage = styled.img`
  width: 40px;
  height: 40px;
  filter: drop-shadow(0 0 6px rgba(90,110,255,0.45));
`;

const LogoText = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLinkStyled = styled(Link)`
  position: relative;
  color: var(--text-secondary-color);
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;

  transition: all 0.3s ease;

  &:hover {
    color: var(--primary-color);
  }

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: -4px;
    width: ${props => (props.active ? "100%" : "0%")};
    height: 2px;
    background-color: var(--primary-color);
    border-radius: 10px;
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

const CTAButton = styled.a`
  background: linear-gradient(90deg, #576bff, #7385ff);
  padding: 0.65rem 1.6rem;
  border-radius: 10px;
  color: white;
  font-weight: 600;
  text-decoration: none;

  box-shadow: 0 8px 20px rgba(90,110,255,0.45);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 14px 32px rgba(90,110,255,0.6);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

/* ===============================
   HAMBURGER MENU
================================*/
const Hamburger = styled.div`
  width: 28px;
  height: 22px;
  position: relative;
  cursor: pointer;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }

  div {
    position: absolute;
    height: 3px;
    width: 100%;
    background: white;
    border-radius: 5px;
    transition: 0.4s ease;
  }

  div:nth-child(1) {
    top: ${props => (props.open ? "10px" : "0")};
    transform: ${props => (props.open ? "rotate(45deg)" : "none")};
  }

  div:nth-child(2) {
    opacity: ${props => (props.open ? "0" : "1")};
  }

  div:nth-child(3) {
    bottom: ${props => (props.open ? "9px" : "0")};
    transform: ${props => (props.open ? "rotate(-45deg)" : "none")};
  }
`;

/* ===============================
   MOBILE MENU DRAWER
================================*/

const MobileMenuWrapper = styled.div`
  position: fixed;
  top: 0;
  right: ${props => (props.open ? "0" : "-100%")};
  width: 75%;
  height: 100vh;
  background: rgba(20,22,45,0.9);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: 0.35s ease;
  padding: 2rem 1.5rem;
  z-index: 2500;
  box-shadow: -5px 0 25px rgba(0,0,0,0.3);
`;

const MobileMenuLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.8rem;
  margin-top: 2.5rem;
`;

const MobileNavLink = styled(Link)`
  color: white;
  font-size: 1.35rem;
  text-decoration: none;
  font-weight: 600;

  transition: 0.3s ease;

  &:hover {
    color: var(--primary-color);
  }
`;

const MobileCTA = styled.a`
  margin-top: 2.8rem;
  display: inline-block;

  background: linear-gradient(90deg, #576bff, #7385ff);
  padding: 0.9rem 1.6rem;
  border-radius: 12px;
  color: white;
  font-weight: 600;
  text-decoration: none;
  font-size: 1.1rem;

  box-shadow: 0 10px 28px rgba(90,110,255,0.45);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

/* ===============================
   DARK OVERLAY
================================*/
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;

  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(4px);

  opacity: ${props => (props.open ? "1" : "0")};
  pointer-events: ${props => (props.open ? "auto" : "none")};
  transition: opacity 0.3s ease;

  z-index: 2000;
`;

/* ===============================
   MAIN COMPONENT
================================*/

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false); // close menu on navigation
  }, [location]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);


  const links = [
    { name: "Home", to: "/" },
    { name: "About Us", to: "/about" },
    { name: "Genie Social", to: "/pricing" },
    { name: "Rewards", to: "/rewards" },
    { name: "$TGEN Token", to: "/token" },
    { name: "Live Results", to: "/live-results" },
    { name: "Insights", to: "/insights" },
    {name: "White Paper", to: "/whitepaper" }
  ];

  return (
    <>
      <Nav scrolled={scrolled}>

        <LogoContainer to="/">
          <LogoImage src={logoImage} alt="Trend Genie Logo" />
          <LogoText>Trend Genie</LogoText>
        </LogoContainer>

        {/* Desktop Links */}
        <NavLinks>
          {links.map((link, i) => (
            <NavLinkStyled 
              key={i}
              to={link.to}
              active={location.pathname === link.to}
            >
              {link.name}
            </NavLinkStyled>
          ))}
        </NavLinks>

        <CTAButton 
          href="https://social.trendgenie.io"
          target="_blank"
        >
          Genie Social
        </CTAButton>

        {/* Hamburger Menu */}
        <Hamburger open={open} onClick={() => setOpen(!open)}>
          <div></div>
          <div></div>
          <div></div>
        </Hamburger>

      </Nav>

      {/* Mobile Overlay */}
      <Overlay open={open} onClick={() => setOpen(false)} />

      {/* Mobile Menu Drawer */}
      <MobileMenuWrapper open={open}>
        <MobileMenuLinks>
          {links.map((link, i) => (
            <MobileNavLink key={i} to={link.to}>
              {link.name}
            </MobileNavLink>
          ))}
        </MobileMenuLinks>

        <MobileCTA 
          href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie"
          target="_blank"
        >
          Download Trend Genie
        </MobileCTA>
      </MobileMenuWrapper>
    </>
  );
};

export default Navbar;
